import {
  mergeTyped,
  useDataStream,
  useNavigator,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { createKoumuten } from '../../../api/fetch-functions/koumuten';
import { KoumutenAPIRequestBodyType } from '../../../api/types/koumuten/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken, useUserDetail } from '../../../global-state/auth-state';
import { fetchKoumutenRelatedList } from '../../../global-state/database/combined';
import { fetchKoumutenList } from '../../../global-state/database/koumuten-list';
import { routes } from '../../../routing/routes';
import { IKoumutenType } from '../../../types/record/koumuten';
import { CreateNewKoumutenDialogView } from './create-new-koumuten-dialog-view';

const formInputStateConfig: FormInputStateConfigType<KoumutenAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.koumutenName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.koumutenName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
}

export const CreateNewKoumutenDialog: FC<Props> = memo(
  ({ open, onClose }: Readonly<Props>) => {
    const token = useToken();
    const { name } = useUserDetail();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IKoumutenType, KoumutenAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      koumutenName$,
      koumutenNameErrors$,
      koumutenNameIsValid$,
      koumutenNameOnInput
    ] = useFormInputState<IKoumutenType, KoumutenAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), koumutenNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const koumutenName = useStreamValue(koumutenName$);
    const koumutenNameErrors = useStreamValue(koumutenNameErrors$);

    /* event handler */

    const navigator = useNavigator();

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (token !== undefined && koumutenName !== undefined) {
            createKoumuten(token)({
              name: koumutenName
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchKoumutenList();
                const koumutenId = res.body.id;
                fetchKoumutenRelatedList(koumutenId);
                navigator(routes.companyList(koumutenId));
              }
            });
          }
        }
      },
      [onClose, koumutenName, token, setFetchResult, submitOnClick, navigator]
    );

    return (
      <CreateNewKoumutenDialogView
        open={open}
        onClose={closeOnClick}
        registererName={name}
        koumutenName={koumutenName}
        koumutenNameErrors={koumutenNameErrors}
        koumutenNameOnInput={koumutenNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

CreateNewKoumutenDialog.displayName = 'CreateNewKoumutenDialog';
